import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Hero from "@/components/home/Hero";
import Catalog from "@/components/home/Catalog";
import RoutesPreview from "@/components/home/RoutesPreview";
import ContactCTA from "@/components/home/ContactCTA";
import ServiceAreas from "@/components/ServiceAreas";
import SeoHead from "@/components/SeoHead";
import { useLang } from "@/contexts/LanguageContext";
import { buildLocalBusinessJsonLd, buildBreadcrumb, BRAND_DISCLAIMER } from "@/lib/seo";
import { pathFor, ROUTE_KEYS } from "@/i18n/routeMap";

export default function Home() {
  const { lang, t } = useLang();
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [v, r, s] = await Promise.all([
          api.get("/vehicles"),
          api.get("/routes"),
          api.get("/settings"),
        ]);
        setVehicles(v.data || []);
        setRoutes(r.data || []);
        setSettings(s.data || {});
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.error("home load", e);
        }
      }
    })();
  }, []);

  const homeSeo = {
    es: {
      title: "Spain Luxury Rentals · Alquiler de coches de lujo en Benidorm y Costa Blanca",
      description: "Alquiler de coches deportivos, muscle cars americanos, SUVs de lujo y GT premium en Benidorm, Alicante y Costa Blanca. Reserva por WhatsApp.",
    },
    en: {
      title: "Spain Luxury Rentals · Luxury car rental in Benidorm and Costa Blanca",
      description: "Sports car, American muscle, luxury SUV and premium GT rentals in Benidorm, Alicante and Costa Blanca. WhatsApp booking.",
    },
    ru: {
      title: "Spain Luxury Rentals · Премиальный прокат авто в Бенидорме и на Коста-Бланке",
      description: "Аренда спорткаров, американских muscle cars, премиальных SUV и GT в Бенидорме, Аликанте и на Коста-Бланке. Бронирование через WhatsApp.",
    },
    fr: {
      title: "Spain Luxury Rentals · Location de voitures de luxe à Benidorm et sur la Costa Blanca",
      description: "Location de voitures de sport, muscle cars américaines, SUV de luxe et GT premium à Benidorm, Alicante et sur la Costa Blanca. Réservation par WhatsApp.",
    },
    de: {
      title: "Spain Luxury Rentals · Luxuswagen mieten in Benidorm und an der Costa Blanca",
      description: "Sportwagen, amerikanische Muscle-Cars, Luxus-SUVs und Premium-GTs in Benidorm, Alicante und an der Costa Blanca mieten. Buchung per WhatsApp.",
    },
    it: {
      title: "Spain Luxury Rentals · Noleggio auto di lusso a Benidorm e Costa Blanca",
      description: "Noleggio di auto sportive, muscle car americane, SUV di lusso e GT premium a Benidorm, Alicante e Costa Blanca. Prenotazione via WhatsApp.",
    },
    nl: {
      title: "Spain Luxury Rentals · Luxe auto huren in Benidorm en aan de Costa Blanca",
      description: "Huur sportwagens, Amerikaanse muscle cars, luxe SUV’s en premium GT’s in Benidorm, Alicante en aan de Costa Blanca. Reserveren via WhatsApp.",
    },
    pl: {
      title: "Spain Luxury Rentals · Wynajem luksusowych samochodów w Benidormie i na Costa Blanca",
      description: "Wynajem aut sportowych, amerykańskich muscle cars, luksusowych SUV-ów i premium GT w Benidormie, Alicante i na Costa Blanca. Rezerwacja przez WhatsApp.",
    },
  };
  const seoTitle = (homeSeo[lang] || homeSeo.en).title;
  const seoDesc = (homeSeo[lang] || homeSeo.en).description;

  const jsonLd = [
    buildLocalBusinessJsonLd(settings),
    buildBreadcrumb([{ name: t.nav.catalog, path: pathFor(ROUTE_KEYS.HOME, lang) }]),
  ];

  return (
    <div data-testid="home-page">
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        image={settings?.heroFallbackImage}
        lang={lang}
        pageKey={ROUTE_KEYS.HOME}
        jsonLd={jsonLd}
      />
      <Hero settings={settings} />
      <Catalog vehicles={vehicles} settings={settings} />
      <section className="bg-[#0C0C0C] border-t border-[#2A2A2A] py-12 px-6 md:px-10" data-testid="brand-disclaimer">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-overline text-[#C9A55A] mb-3">Ferrari · Lamborghini · BMW M · G-Class · Wagoneer</div>
          <p className="text-sm text-[#F5F1EB]/65 font-light leading-relaxed">{BRAND_DISCLAIMER[lang] || BRAND_DISCLAIMER.en}</p>
        </div>
      </section>
      <RoutesPreview routes={routes} />
      <ServiceAreas lang={lang} />
      <ContactCTA settings={settings} />
    </div>
  );
}
